/**
 * TemplateProfilesEditor — Verwaltungs-UI fuer Mail-Vorlagen einer Stage
 * (Phase 8 Plan 03, Bug-Session 29.05.).
 *
 * Layout: 2-Spalten
 *   Links (w-60):  Liste aller Profile dieser Stage + "+ Neue Vorlage"-Button
 *   Rechts (flex-1): Profil-Name + Subject + TipTap-Body (inkl. eigener
 *                   Variablen-Toolbar) + Vorschau + Speichern + Test-Send
 *
 * Bug-Session 29.05. Aenderungen:
 *   - handleAddNew sendet sinnvolle Default-Werte (Subject = "Neuer Betreff",
 *     bodyHtml = "<p>Hallo {{verein_name}},</p><p></p>"). Backend rejected
 *     leeren Subject mit HTTP 400.
 *   - createError-Banner zeigt Anlege-Fehler oben ueber der Liste sichtbar,
 *     auch wenn noch kein selectedProfile da ist.
 *   - Doppelter Variablen-Block entfernt — TipTapEditor hat eigenen Footer.
 *   - Save-Erfolg als gruener Banner statt klein-Text am Rand.
 *   - Test-Send reicht jetzt templateProfileId mit (Backend nutzt diese
 *     Profile statt der Default-Vorlage).
 *   - Test-Info-Text bereinigt (keine internen Plan-Referenzen).
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { TipTapEditor } from '../editor/TipTapEditor';
import { sanitizeEditorHtml } from '../editor/sanitizeEditorHtml';
import { TemplateListItem } from './TemplateListItem';
import { leadsApi } from '../../services/leadsApi';
import type { LeadTemplateProfile, LeadTemplateKey, SignatureProfile } from '../../types';

// ── Pure Hilfsfunktionen (exportiert fuer Unit-Tests) ──────────────────────

export function buildDeleteConfirmText(profile: LeadTemplateProfile): string {
  if (profile.isDefault) {
    return (
      `Vorlage "${profile.name}" wirklich loeschen?\n\n` +
      'Die Default-Vorlage wird geloescht. Die aelteste verbleibende Vorlage dieser Stage wird automatisch zum neuen Default.'
    );
  }
  return `Vorlage "${profile.name}" wirklich loeschen?`;
}

export function validateProfileName(name: string): string | null {
  const trimmed = name.trim();
  if (trimmed.length === 0) return 'Name darf nicht leer sein.';
  if (trimmed.length > 100) return 'Name darf maximal 100 Zeichen lang sein.';
  return null;
}

export function validateSubject(subject: string): string | null {
  const trimmed = subject.trim();
  if (trimmed.length === 0) return 'Subject darf nicht leer sein.';
  if (subject.length > 500) return 'Subject darf maximal 500 Zeichen lang sein.';
  return null;
}

const STAGE_DEFAULT_NAMES: Record<LeadTemplateKey, string> = {
  erstkontakt: 'Erstkontakt',
  followup_1: 'Follow-up 1',
  followup_2: 'Follow-up 2',
};

// ── Hauptkomponente ────────────────────────────────────────────────────────

interface Props {
  templateKey: LeadTemplateKey;
}

export default function TemplateProfilesEditor({ templateKey }: Props) {
  const [profiles, setProfiles] = useState<LeadTemplateProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [subjectInput, setSubjectInput] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // createError separat — wird oben in der Liste angezeigt (sichtbar auch ohne selectedProfile)
  const [createError, setCreateError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  // Subject-Input-Ref fuer Variable-Insert an Cursor-Position (Iter 7)
  const subjectInputRef = useRef<HTMLInputElement | null>(null);

  // Signatur-Profile fuer Test-Send-Dropdown (Phase 7 D-04 + Bug B 30.05. Persistenz)
  const [signatureProfiles, setSignatureProfiles] = useState<SignatureProfile[]>([]);
  // Bug B (30.05.): testSignatureProfileId wird beim Profil-Wechsel auf
  // profile.defaultSignatureProfileId gesetzt (oder User-Default als Fallback) und
  // beim Save mit der Vorlage persistiert. So bleibt die Sig-Auswahl pro Vorlage
  // erhalten — vorher war's Browser-only und ging bei Reload verloren.
  const [testSignatureProfileId, setTestSignatureProfileId] = useState<number | null>(null);
  const [testing, setTesting] = useState(false);
  const [testInfo, setTestInfo] = useState<string | null>(null);

  const selectedProfile = profiles.find((p) => p.id === selectedId) ?? null;

  const loadProfiles = useCallback(async () => {
    try {
      const list = await leadsApi.templateProfiles.byStage(templateKey);
      setProfiles(list);
      return list;
    } catch (err: any) {
      setError('Vorlagen konnten nicht geladen werden.');
      return [];
    }
  }, [templateKey]);

  // Initial-Load + Default-Auswahl bei Stage-Wechsel
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelectedId(null);
    setNameInput('');
    setSubjectInput('');
    setBodyHtml('');
    setSavedAt(null);
    setSaveError(null);
    setCreateError(null);

    leadsApi.templateProfiles.byStage(templateKey)
      .then((list) => {
        if (cancelled) return;
        setProfiles(list);
        setLoading(false);
        const def = list.find((p) => p.isDefault) ?? list[0];
        if (def) selectProfile(def);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Vorlagen konnten nicht geladen werden.');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
    // selectProfile ist stabil genug — kein loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateKey]);

  // Signatur-Profile fuer Test-Send-Dropdown laden (1× pro Mount).
  // Bug B (30.05.): Default fuer initialen Mount ist die User-Default-Sig — wenn der
  // User danach ein Profil oeffnet, ueberschreibt selectProfile diesen Wert mit der
  // vorlage-spezifischen Default-Sig (falls gesetzt).
  useEffect(() => {
    leadsApi.signatureProfiles.list()
      .then((profs) => {
        setSignatureProfiles(profs);
        const def = profs.find((p) => p.isDefault);
        if (def) setTestSignatureProfileId(def.id);
      })
      .catch(() => { /* bleibt leer */ });
  }, []);

  function selectProfile(profile: LeadTemplateProfile) {
    setSelectedId(profile.id);
    setNameInput(profile.name);
    setSubjectInput(profile.subject);
    setBodyHtml(profile.bodyHtml);
    setSaveError(null);
    setSavedAt(null);
    setTestInfo(null);
    setPreview(null);
    // Bug B (30.05.): Sig-Dropdown auf vorlage-spezifische Sig setzen wenn vorhanden,
    // sonst User-Default als Fallback. So bleibt die Auswahl pro Vorlage erhalten.
    if (profile.defaultSignatureProfileId != null) {
      setTestSignatureProfileId(profile.defaultSignatureProfileId);
    } else {
      const userDefault = signatureProfiles.find((p) => p.isDefault);
      setTestSignatureProfileId(userDefault ? userDefault.id : null);
    }
  }

  async function handleAddNew() {
    setSaveError(null);
    setCreateError(null);
    try {
      // BugFix 29.05.: Backend verlangt nicht-leeren Subject (validateCreateTemplateProfile).
      // Frueher schickte handleAddNew subject='' → HTTP 400 → kein Profil angelegt.
      // Defaults sind so generisch dass User sie eh anpassen wird.
      // PR 1 (01.06.): defaultSignatureProfileId mit globaler Default-Sig
      // vorbelegen, damit neue Vorlagen nicht ohne Signatur rauspuschen wenn
      // der User vergisst, eine zu waehlen (siehe Iter-7-Bug: Follow-up 1+2 hatten
      // NULL → Mails ohne Sig).
      const globalDefaultSig = signatureProfiles.find((p) => p.isDefault);
      const created = await leadsApi.templateProfiles.create({
        templateKey,
        name: `Neue ${STAGE_DEFAULT_NAMES[templateKey]}-Vorlage`,
        subject: 'Neuer Betreff',
        bodyHtml: '<p>Hallo {{verein_name}},</p><p></p>',
        bodyText: 'Hallo {{verein_name}},',
        isDefault: false,
        defaultSignatureProfileId: globalDefaultSig?.id ?? null,
      });
      const updated = await loadProfiles();
      const fresh = updated.find((p) => p.id === created.id) ?? created;
      selectProfile(fresh);
    } catch (err: any) {
      // Backend-Fehlermeldungen koennen mehrere Felder enthalten (validateCreateTemplateProfile.details).
      const details = err?.response?.data?.error?.details;
      const detailMsg = Array.isArray(details) && details.length > 0
        ? details.map((d: any) => `${d.field}: ${d.message}`).join(', ')
        : null;
      setCreateError(
        detailMsg
          || err?.response?.data?.error?.message
          || err?.message
          || 'Anlegen fehlgeschlagen.',
      );
    }
  }

  async function handleSave() {
    if (!selectedProfile) return;
    const nameError = validateProfileName(nameInput);
    if (nameError) { setSaveError(nameError); return; }
    const subjectError = validateSubject(subjectInput);
    if (subjectError) { setSaveError(subjectError); return; }
    setSaving(true);
    setSaveError(null);
    try {
      const sanitized = sanitizeEditorHtml(bodyHtml);
      await leadsApi.templateProfiles.update(selectedProfile.id, {
        name: nameInput.trim(),
        subject: subjectInput.trim(),
        bodyHtml: sanitized,
        // Bug B (30.05.): Sig-Auswahl pro Vorlage persistieren. null = "Keine Signatur" (explizit).
        defaultSignatureProfileId: testSignatureProfileId,
      });
      setSavedAt(new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }));
      const updated = await loadProfiles();
      const fresh = updated.find((p) => p.id === selectedProfile.id);
      if (fresh) {
        // selectedId beibehalten + bodyHtml aktualisieren falls Backend was geaendert hat.
        // BugFix 4 (TipTap content-sync) sorgt dafuer dass der Editor das uebernimmt.
        setSelectedId(fresh.id);
        setBodyHtml(fresh.bodyHtml);
      }
    } catch (err: any) {
      setSaveError(err?.response?.data?.error?.message ?? err?.message ?? 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSetDefault(profile: LeadTemplateProfile) {
    try {
      await leadsApi.templateProfiles.setDefault(profile.id);
      await loadProfiles();
    } catch (err: any) {
      setSaveError(err?.response?.data?.error?.message ?? err?.message ?? 'Default setzen fehlgeschlagen.');
    }
  }

  async function handleDelete(profile: LeadTemplateProfile) {
    const confirmed = window.confirm(buildDeleteConfirmText(profile));
    if (!confirmed) return;
    try {
      await leadsApi.templateProfiles.remove(profile.id);
      const updated = await loadProfiles();
      const next = updated[0];
      if (next) selectProfile(next);
      else {
        setSelectedId(null);
        setNameInput('');
        setSubjectInput('');
        setBodyHtml('');
      }
    } catch (err: any) {
      // Backend returnt 400 LAST_TEMPLATE_OF_STAGE wenn letzte Vorlage geloescht
      // werden soll. UI sollte das eh blocken (TemplateListItem disabled), aber
      // Race-safe: hier nochmal die Backend-Fehlermeldung anzeigen.
      setSaveError(err?.response?.data?.error?.message ?? err?.message ?? 'Loeschen fehlgeschlagen.');
    }
  }

  function handlePreview() {
    const vars: Record<string, string> = {
      verein_name: 'FC Beispiel',
      verein_contact_person: 'Max Mustermann',
      verein_email: 'max@fc-beispiel.de',
      actor_name: 'Simon Kuerbs',
      signature: '<p>Simon Kuerbs<br>Teamshops &amp; Partnerbetreuung</p>',
    };
    const replaced = bodyHtml.replace(/\{\{(\w+)\}\}/g, (m, k) => vars[k] ?? m);
    setPreview(replaced);
  }

  async function handleTestSend() {
    if (!selectedProfile) return;
    setTesting(true);
    setTestInfo(null);
    setSaveError(null);
    try {
      // BugFix 29.05.: reicht templateProfileId mit damit das aktuell editierte
      // Profil getestet wird statt der Default-Vorlage der Stage.
      const res = await leadsApi.templates.testSend(templateKey, {
        signatureProfileId: testSignatureProfileId,
        templateProfileId: selectedProfile.id,
      });
      setTestInfo(`Test-Mail an ${res.sentTo} gesendet — Vorlage "${selectedProfile.name}".`);
    } catch (err: any) {
      setSaveError(err?.response?.data?.error?.message ?? err?.message ?? 'Test-Send fehlgeschlagen.');
    } finally {
      setTesting(false);
    }
  }

  if (loading) return <p className="text-sm text-gray-500">Lade Vorlagen…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;

  const isOverSoftLimit = profiles.length >= 10;
  const isLastInStage = profiles.length === 1;

  return (
    <div className="flex gap-4 min-h-[500px]">
      {/* ── Linke Spalte ───────────────────────────────────────────── */}
      <div className="w-60 shrink-0 flex flex-col gap-2">
        <button
          type="button"
          onClick={handleAddNew}
          className="w-full px-3 py-2 text-sm font-semibold rounded bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d] transition-colors"
        >
          + Neue Vorlage
        </button>
        {/* createError oben ueber der Liste — wird sichtbar auch wenn kein
            selectedProfile da ist (frueher Bug: Backend-400 ging ins Leere) */}
        {createError && (
          <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
            Anlegen fehlgeschlagen: {createError}
          </p>
        )}
        {isOverSoftLimit && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            Sehr viele Vorlagen ({profiles.length}) — Uebersicht leidet.
          </p>
        )}
        <div className="flex flex-col gap-0.5 overflow-y-auto">
          {profiles.map((profile) => (
            <TemplateListItem
              key={profile.id}
              profile={profile}
              isActive={profile.id === selectedId}
              isLastInStage={isLastInStage}
              onSelect={() => selectProfile(profile)}
              onSetDefault={() => handleSetDefault(profile)}
              onDelete={() => handleDelete(profile)}
            />
          ))}
          {profiles.length === 0 && (
            <p className="text-xs text-gray-400 px-2 py-2">
              Noch keine Vorlagen — leg eine an.
            </p>
          )}
        </div>
      </div>

      {/* ── Rechte Spalte ──────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col gap-3">
        {selectedProfile ? (
          <>
            {/* Header mit Profil-Name + Default-Hint */}
            <div className="flex items-center justify-between gap-2 pb-2 border-b border-[#DAD9D9]">
              <h3 className="text-sm font-semibold text-[#1A1A1A]">
                Bearbeite: <span className="font-bold">{selectedProfile.name}</span>
                {selectedProfile.isDefault && (
                  <span className="ml-2 text-xs font-normal text-amber-700">⭐ Default dieser Stage</span>
                )}
              </h3>
              {savedAt && (
                <span className="text-xs px-2 py-1 rounded bg-green-50 text-green-800 border border-green-200">
                  ✓ Gespeichert um {savedAt}
                </span>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-[#1A1A1A] mb-1">Profil-Name</label>
              <input
                type="text"
                value={nameInput}
                onChange={(e) => { setNameInput(e.target.value); setSavedAt(null); }}
                maxLength={100}
                className="w-full border border-[#DAD9D9] rounded px-3 py-1.5 text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]"
                placeholder="z.B. Standard, Saisonstart…"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#1A1A1A] mb-1">Betreff</label>
              <input
                ref={subjectInputRef}
                type="text"
                value={subjectInput}
                onChange={(e) => { setSubjectInput(e.target.value); setSavedAt(null); }}
                maxLength={500}
                className="w-full border border-[#DAD9D9] rounded px-3 py-1.5 text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]"
                placeholder="Mail-Subject — Variablen via Button unten einfuegen"
              />
              {/* Iter 7: Variable-Insert direkt unter Subject-Feld.
                  Klick fuegt `{{varname}}` an der Cursor-Position ein.
                  Im Subject sind nur einfache Text-Variablen sinnvoll — kein
                  {{signature}} (Mehrzeilen-HTML im Subject ergibt keinen Sinn). */}
              <div className="mt-1 flex flex-wrap gap-1">
                <span className="text-xs text-gray-500 mr-1">Variablen in Betreff:</span>
                {['verein_name', 'verein_contact_person', 'verein_email', 'actor_name'].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => {
                      const input = subjectInputRef.current;
                      const insert = `{{${v}}}`;
                      if (input) {
                        const start = input.selectionStart ?? subjectInput.length;
                        const end = input.selectionEnd ?? subjectInput.length;
                        const next = subjectInput.slice(0, start) + insert + subjectInput.slice(end);
                        setSubjectInput(next);
                        setSavedAt(null);
                        // Cursor hinter Insert positionieren (nach React-Update)
                        requestAnimationFrame(() => {
                          input.focus();
                          const pos = start + insert.length;
                          input.setSelectionRange(pos, pos);
                        });
                      } else {
                        setSubjectInput(subjectInput + insert);
                        setSavedAt(null);
                      }
                    }}
                    className="px-2 py-0.5 text-xs font-mono bg-[#DAD9D9] text-[#1A1A1A] rounded hover:bg-gray-300 transition-colors"
                  >
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#1A1A1A] mb-1">HTML-Body</label>
              {/* TipTapEditor hat eingebaute Variablen-Toolbar + Bild-Einfuegen
                  am Footer. Doppelter Block entfernt (BugFix 29.05.). */}
              <TipTapEditor
                key={selectedProfile.id}
                content={bodyHtml}
                onChange={(html) => { setBodyHtml(html); setSavedAt(null); }}
                placeholder="Mail-Text mit Variablen…"
                enableImage
              />
            </div>
            {saveError && <p className="text-sm text-red-600">{saveError}</p>}
            {testInfo && <p className="text-sm text-green-700">{testInfo}</p>}
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handlePreview}
                className="px-4 py-2 text-sm font-medium rounded bg-[#1A1A1A] text-white hover:bg-gray-800 transition-colors"
              >
                Vorschau
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium rounded bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d] transition-colors disabled:opacity-50"
              >
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
              {signatureProfiles.length > 0 && (
                <select
                  className="border border-[#DAD9D9] rounded px-3 py-2 text-sm"
                  value={testSignatureProfileId ?? ''}
                  onChange={(e) => {
                    setTestSignatureProfileId(e.target.value ? Number(e.target.value) : null);
                    // Bug B (30.05.): Aenderung markiert die Vorlage als ungespeichert
                    // — der "Gespeichert"-Banner verschwindet bis zum naechsten Save.
                    setSavedAt(null);
                  }}
                  title="Signatur fuer diese Vorlage waehlen (wird pro Vorlage persistiert)"
                >
                  <option value="">Keine Signatur</option>
                  {signatureProfiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}{p.isDefault ? ' (Standard)' : ''}
                    </option>
                  ))}
                </select>
              )}
              <button
                type="button"
                onClick={handleTestSend}
                disabled={testing}
                className="px-4 py-2 text-sm font-medium rounded border border-[#1A1A1A] text-[#1A1A1A] hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                {testing ? 'Sendet…' : 'Test an mich senden'}
              </button>
            </div>
            {preview && (
              <div className="mt-4 border border-[#DAD9D9] rounded p-4 bg-gray-50">
                <h4 className="text-sm font-semibold text-[#1A1A1A] mb-2">Vorschau</h4>
                <iframe
                  srcDoc={preview}
                  title="HTML-Vorschau"
                  sandbox="allow-same-origin"
                  className="w-full h-64 border border-[#DAD9D9] rounded bg-white"
                />
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-gray-400">
              Waehle eine Vorlage aus oder lege eine neue an.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
